import jwtDecode from "jwt-decode";

import { resetPostHog } from "@/lib/posthog";
import { notifyAuthenticationStateChanged } from "@/lib/authentication-events";

import Client from "../client";
import Settings from "../settings";

const STORAGE_WORD = "TOKEN_LOGIN";

// Sessão deslizante: renova o token quando ele tem mais de 7 dias (ou já venceu),
// em vez de deslogar pelo relógio. Assim qualquer visita reemite 1 ano de validade.
const RENEW_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

export default class AuthService {
  private static renewInFlight = false;

  static async isAuthenticated() {
    const token = localStorage.getItem(STORAGE_WORD);
    if (!token) {
      return;
    }

    let tokenDecode: any;
    try {
      tokenDecode = jwtDecode(token);
    } catch (_e) {
      // Token corrompido no storage — sem como recuperar.
      this.logout();
      return false;
    }

    // NUNCA desloga por expiração no relógio do cliente: o login só cai se o
    // backend rejeitar a renovação de forma definitiva (token inválido ou
    // usuário/conta removidos). Falha de rede mantém a sessão.
    const issued = Number(tokenDecode.issued || 0);
    const shouldRenew =
      tokenDecode.expires !== "never" &&
      Boolean(tokenDecode.userId) &&
      Date.now() > issued + RENEW_AFTER_MS &&
      !this.renewInFlight;

    if (shouldRenew) {
      this.renewInFlight = true;
      try {
        const client = new Client();
        const response = await client.renewSession();
        if (response?.token) {
          localStorage.setItem(STORAGE_WORD, response.token);
        }
      } catch (error: any) {
        // A renovação é APENAS otimização de validade (sessão deslizante) — o
        // token atual continua aceito pelo backend (assinatura válida mesmo com
        // data vencida). Por isso NUNCA deslogamos aqui: um 401/404 nesta sonda
        // pode ser um falso-negativo transitório (blip de DB no findById do
        // usuário, janela de rotação de secret JWT, ou visão de coleção
        // divergente em conta legada) e destruir a sessão de um cliente pagante
        // "do nada" — exatamente o bug de deslogamento espontâneo no Manager.
        // Se o usuário foi de fato removido, a próxima chamada REAL de API
        // retorna 401 e é tratada no seu próprio fluxo; a sonda de boot não pode
        // ser destrutiva. Mantém a sessão em qualquer falha de renovação.
        console.warn(
          "[AuthService] renovação de sessão falhou (sessão mantida):",
          error?.code ?? "",
          error?.message || error?.error || error
        );
      } finally {
        this.renewInFlight = false;
      }
    }

    return true;
  }

  static async setToken(token) {
    localStorage.setItem(STORAGE_WORD, token);
    await Settings.loadSettings();
    notifyAuthenticationStateChanged();
    // stop js execution

    return {
      error: false,
      token: token,
    };
  }

  static async changeSessionOnAccount({ token, accountId, world }) {
    const client = new Client();
    const response = await client.changeSessionOnAccount({
      token,
      accountId,
      world,
    });
    if (!response.error) {
      await this.setToken(response.token);
    }
    return response;
  }

  static async login({ username, password, preferrAccount }) {
    const client = new Client();
    const response = await client.auth({ username, password, preferrAccount });
    if (!response.error) {
      await this.setToken(response.token);
    }
    return response;
  }

  /** Login por código de email (sem senha): valida o código e grava a sessão. */
  static async loginWithEmailCode({ email, code }: { email: string; code: string }) {
    const client = new Client();
    const response = await client.verifyLoginCode(email, code);
    if (!response.error) {
      await this.setToken(response.token);
    }
    return response;
  }

  static async loginWithGoogle({ credential, preferrAccount }) {
    const client = new Client();
    const response = await client.authGoogle({ credential, preferrAccount });
    if (!response.error) {
      await this.setToken(response.token);
    }
    return response;
  }

  static async loginWithApple({ code, id_token, preferrAccount }: { code: string; id_token: string; preferrAccount: string }) {
    const client = new Client();
    const response = await client.authApple({ code, id_token, preferrAccount });
    if (!response.error) {
      await this.setToken(response.token);
    }
    return response;
  }

  static isLogged() {
    try {
      return localStorage.getItem(STORAGE_WORD);
    } catch (_e) {
      return null;
    }
  }

  static async logout() {
    Settings.unloadSettings();
    localStorage.removeItem(STORAGE_WORD);
    sessionStorage.removeItem("gamefik_professor_modal_session");
    sessionStorage.removeItem("gamefik:mural-ai:pending-operation");
    resetPostHog();
    notifyAuthenticationStateChanged();
  }

  static async sendNewPassword(data, uid, token) {
    const client = new Client();
    return client.sendNewPassword(data, uid, token);
  }

  static async acceptInviteAndLogin(
    data: { newPassword: string },
    uid: string,
    token: string
  ) {
    const client = new Client();
    const response = await client.acceptInviteAndLogin(data, uid, token);
    await this.setToken(response.token);
    return response;
  }
}
